import { VNode, RefObject } from 'preact';
import { createPortal, useContext, useLayoutEffect, useMemo, useState } from 'preact/compat';
import { LayerContext } from './LayerContext';

const _LOGICAL_PARENT = '__oj_logical_parent';

type Props = {
  /**
   * The content to be re-parented using Portal
   */
  children: VNode;
  /**
   * Logical parent of the re-parented content. Typically the 'launcher' associated
   * with the popup.
   */
  logicalParentRef?: RefObject<Element> | null;
};

type LayerElement = HTMLDivElement & {
  __oj_logical_parent?: Element | null;
};

const Layer = (props: Props) => {
  const context = useContext(LayerContext);
  const defaultHost = context.getHost?.();
  const host = useMemo(() => defaultHost, [defaultHost]);
  const [layerContainer, setLayerContainer] = useState<Element | null>(null);

  // Really need useLayoutEffect instead of useEffect here. Otherwise the re-parented
  // content does not get re-rendered with valid ref's
  useLayoutEffect(() => {
    if (!host) return;
    const doc = host.ownerDocument ?? document;
    const layer: LayerElement = doc.createElement('div');

    if (props.logicalParentRef) {
      layer[_LOGICAL_PARENT] = props.logicalParentRef.current;
    }

    host.appendChild(layer);
    setLayerContainer(layer);

    return () => {
      if (host && layer && host.contains(layer)) {
        delete layer[_LOGICAL_PARENT];
        host.removeChild(layer);
      }
      setLayerContainer(null);
    };
  }, [host]);

  return layerContainer && createPortal(props.children, layerContainer);
};

export default Layer;
